import { useState } from "react";
import { toast } from "sonner";
import { Send, FileText, CheckCircle2, RotateCcw, Ban, Trash2, BellRing, Link2, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, openAuthedPdf } from "@/lib/api";
import { todayLocal } from "@/lib/format";

const PaidPanel = ({ onConfirm, onCancel, busy }) => {
  const [method, setMethod] = useState("bank");
  const [day, setDay] = useState(todayLocal());
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="invoice-paid-panel">
      <div className="space-y-1"><p className="text-xs text-muted-foreground">Paid by</p>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="w-40" data-testid="invoice-paid-method"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="bank">Bank transfer</SelectItem><SelectItem value="card">Card</SelectItem>
            <SelectItem value="cash">Cash</SelectItem><SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1"><p className="text-xs text-muted-foreground">Paid on</p><Input type="date" value={day} max={todayLocal()} onChange={(e) => setDay(e.target.value)} className="w-40" data-testid="invoice-paid-date" /></div>
      <Button disabled={busy} onClick={() => onConfirm({ method, paid_on: day })} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="invoice-paid-confirm">Confirm paid</Button>
      <Button variant="ghost" onClick={onCancel} className="rounded-full">Cancel</Button>
    </div>
  );
};

export const InvoiceActions = ({ inv, dirty, save, onUpdated, onDeleted }) => {
  const [busy, setBusy] = useState("");
  const [paying, setPaying] = useState(false);
  const open = !["paid", "void"].includes(inv.status);
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const r = await fn(); if (r && r.id) onUpdated(r); if (msg && r !== null) toast.success(msg); return r; }
    catch (e) { toast.error(e?.response?.data?.detail || e.message || "Something went wrong"); return null; }
    finally { setBusy(""); }
  };
  const send = () => run("send", async () => {
    if (dirty && !(await save())) return null;
    return api.sendInvoice(inv.id);
  }, inv.status === "draft" ? "Invoice sent" : "Invoice re-sent");
  const pdf = () => run("pdf", async () => { if (dirty) await save(); await openAuthedPdf(`/invoices/${inv.id}/pdf`); });
  const copy = async () => { await navigator.clipboard?.writeText(inv.pay_url).catch(() => {}); toast.success("Pay link copied"); };
  const B = ({ k, icon: Icon, children, ...p }) => (
    <Button disabled={!!busy} variant="outline" className="rounded-full" {...p}>{busy === k ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Icon className="w-4 h-4 mr-2" />}{children}</Button>
  );
  if (paying) return <PaidPanel busy={!!busy} onCancel={() => setPaying(false)} onConfirm={(b) => run("paid", () => api.markInvoicePaid(inv.id, b), "Marked paid").then(() => setPaying(false))} />;
  return (
    <div className="flex flex-wrap gap-2" data-testid="invoice-actions">
      {open && <B k="send" icon={Send} onClick={send} variant="default" className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="invoice-send-btn">{inv.status === "draft" ? "Send invoice" : "Resend"}</B>}
      {open && dirty && <B k="save" icon={Save} onClick={() => run("save", save, "Invoice saved")} data-testid="invoice-save-btn">Save changes</B>}
      <B k="pdf" icon={FileText} onClick={pdf} data-testid="invoice-pdf-btn">{inv.status === "draft" ? "Preview PDF" : "PDF"}</B>
      {open && <B k="paid" icon={CheckCircle2} onClick={() => setPaying(true)} data-testid="invoice-mark-paid-btn">Mark paid</B>}
      {inv.status === "sent" && <B k="remind" icon={BellRing} onClick={() => run("remind", () => api.remindInvoice(inv.id), "Reminder sent")} data-testid="invoice-remind-btn">Send reminder</B>}
      {inv.pay_url && inv.status !== "void" && <B k="copy" icon={Link2} onClick={copy} data-testid="invoice-copy-link-btn">Copy pay link</B>}
      {inv.status === "paid" && <B k="unpaid" icon={RotateCcw} onClick={() => run("unpaid", () => api.markInvoiceUnpaid(inv.id), "Marked unpaid")} data-testid="invoice-mark-unpaid-btn">Mark unpaid</B>}
      {(inv.status === "sent" || (inv.status === "draft" && inv.number)) && <B k="void" icon={Ban} variant="ghost" className="rounded-full text-destructive hover:text-destructive"
        onClick={() => window.confirm(`Void ${inv.number}? The client will see it as cancelled and nothing will be owing.`) && run("void", () => api.voidInvoice(inv.id), "Invoice voided")} data-testid="invoice-void-btn">Void</B>}
      {inv.status === "draft" && !inv.number && <B k="del" icon={Trash2} variant="ghost" className="rounded-full text-destructive hover:text-destructive"
        onClick={() => window.confirm("Delete this draft invoice?") && run("del", async () => { await api.deleteInvoice(inv.id); onDeleted(); }, "Draft deleted")} data-testid="invoice-delete-btn">Delete draft</B>}
    </div>
  );
};
